import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    loading: true, user: null, workspace: null, workspaces: [],
    agreementRequired: false, agreementVersion: null,
  });

  const refresh = useCallback(async () => {
    // Magic-link login sets access_token_client (JS-readable) — hydrate into
    // the same localStorage key axios reads on every request. Also strip the
    // cookie so we don't keep copying stale values.
    try {
      const m = document.cookie.match(/(?:^|;\s*)access_token_client=([^;]+)/);
      if (m && m[1] && !localStorage.getItem("cb_token")) {
        localStorage.setItem("cb_token", decodeURIComponent(m[1]));
        document.cookie = "access_token_client=; Max-Age=0; path=/; secure; samesite=none";
      }
    } catch { /* ignore */ }
    try {
      const [meRes, wsRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/workspaces").catch(() => ({ data: { workspaces: [] } })),
      ]);
      setState({
        loading: false,
        user: meRes.data.user,
        workspace: meRes.data.workspace,
        workspaces: wsRes.data?.workspaces || [],
        agreementRequired: !!meRes.data.agreement_required,
        agreementVersion: meRes.data.agreement_version || null,
      });
    } catch {
      setState({ loading: false, user: null, workspace: null, workspaces: [],
                 agreementRequired: false, agreementVersion: null });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("cb_token", data.token);
    await refresh();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("cb_token", data.token);
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cb_token");
    setState({ loading: false, user: null, workspace: null, workspaces: [],
               agreementRequired: false, agreementVersion: null });
  };

  const switchWorkspace = async (workspaceId) => {
    const { data } = await api.post(`/workspaces/${workspaceId}/switch`);
    if (data.token) localStorage.setItem("cb_token", data.token);
    await refresh();
    return data;
  };

  const createWorkspace = async (name) => {
    const { data } = await api.post("/workspaces", { name });
    // Switch into the new workspace immediately.
    await switchWorkspace(data.id);
    return data;
  };

  const role = state.workspace?.role || null;
  const isAdmin = role === "admin";
  const canWrite = role === "admin" || role === "member";

  return (
    <AuthContext.Provider
      value={{
        ...state, role, isAdmin, canWrite,
        login, register, logout, refresh,
        switchWorkspace, createWorkspace,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
